"use client";

/**
 * AmbientBackground — ambient layer for Scene 0 (and optionally other scenes).
 *
 * Three stacked fixed layers behind scene content:
 *   1. Mood gradient — low-opacity amber radial glow anchored top-right desktop
 *      (matches wireframe §2.2). Fades in 600ms on mount.
 *   2. Noise texture — inline SVG data URI (feTurbulence baseFrequency 0.9).
 *      Opacity 0.035, no animation. Removes digital flatness.
 *   3. Radial vignette — darkens edges to focus center. Apple product-page trick.
 *
 * Reduced-motion: gradient fade-in collapsed to instant render. Noise +
 * vignette never animate.
 *
 * Reuse note: this replicates the niche-v3 OpeningHookScene ambient stack
 * (gradient + noise + vignette) but with a fixed amber palette instead of
 * per-niche mood accent — landing has no niche data to drive mood choice.
 */

import { motion, useReducedMotion } from "framer-motion";

const NOISE_SVG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")";

interface AmbientBackgroundProps {
  /**
   * Gradient origin — "top-right" (desktop hero default per wireframe) or
   * "center" (tablet/mobile softer variant).
   */
  origin?: "top-right" | "center";
  className?: string;
}

export function AmbientBackground({
  origin = "top-right",
  className = "",
}: AmbientBackgroundProps) {
  const reduced = useReducedMotion();

  const gradientStyle =
    origin === "top-right"
      ? "radial-gradient(ellipse at top right, rgba(245,158,11,0.08), transparent 60%)"
      : "radial-gradient(ellipse at center, rgba(245,158,11,0.07), transparent 65%)";

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {/* Layer 1 — mood gradient */}
      <motion.div
        className="absolute inset-0"
        style={{ backgroundImage: gradientStyle }}
        initial={reduced ? { opacity: 1 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: reduced ? 0 : 0.6, ease: "easeOut" }}
      />

      {/* Layer 2 — noise texture (static) */}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{ backgroundImage: NOISE_SVG }}
      />

      {/* Layer 3 — radial vignette darkens edges */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_transparent_55%,_rgba(0,0,0,0.4)_100%)]" />
    </div>
  );
}
